// @flow
import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import { defineMessages, FormattedMessage, intlShape } from 'react-intl';
import { FormValidator } from '../../../../utils/form/index';
import update from 'immutability-helper';
import classNames from 'classnames/bind';
import ImportantDataForm from './ImportantData/ImportantDataForm';
import Dropzone from 'react-dropzone';
import Loading from '../../../../elements/Loading/Loading';
import Modal from '../../../../elements/Modal/Modal';
import SuccessErrorModal from '../../../../elements/SuccessErrorModal/SuccessErrorModal';
import PunchCardContainer from '../../../punch-card/containers/PunchCardContainer';

import style from './EditProfile.scss';
import commonStyle from '../../PreferencesViewCommon.scss';
import defaultPic from '../../../../assets/img/profileplaceholder.jpg';
import avatarStyle from '../../../../assets/styles/avatar.scss';
import buttonStyle from '../../../../assets/styles/button.scss';
import icons from '../../../../assets/styles/icons.scss';
import modalStyle from '../../../../assets/styles/modal.scss';
import boxStyle from '../../../../assets/styles/box.scss';
import inputStyle from '../../../../assets/styles/input.scss';

import type { User } from '../../../../entities';
import type { HTTPError } from '../../../../error';
import type { FormValidationResult } from '../../../../utils/form';
import type { ProfileData } from '../duck';

type Props = {
  +editProfile: (profileData: ProfileData) => void;
  +editProfileImage: (profileData: ProfileData) => void;
  +resetEditProfileState: () => void;
  +user: User;
  +dataEditSuccessful: boolean;
  +isPutting?: boolean;
  +isUploadingImage?: boolean;
  +putError?: HTTPError;
  +resetAlertState: () => void;
};

type State = {
  image: {
    value: string;
    pristine: boolean;
    errors?: string[];
    previewImage?: string;
  };
  isUploadingImage?: boolean;
  firstName: {
    value: string;
    pristine: boolean;
    errors?: string[];
  };
  lastName: {
    value: string;
    pristine: boolean;
    errors?: string[];
  };
  modalVisible: boolean;
};

const messages = defineMessages({
  title: {
    id: 'preferences.editProfile.document.title',
  },
  description: {
    id: 'preferences.editProfile.document.description',
  },
  maxSize: {
    id: 'preferences.editProfile.imageMaxSize',
  },
  profilePicture: {
    id: 'preferences.editProfile.profilePicture',
  },
  profilePictureSubtitle: {
    id: 'preferences.editProfile.profilePicture.subtitle',
  },
  size: {
    id: 'preferences.editProfile.image.error.size',
  },
  format: {
    id: 'preferences.editProfile.image.error.format',
  },
  button: {
    saveChange: {
      id: 'preferences.editProfile.saveChange',
    },
    cancelAccount: {
      id: 'preferences.editProfile.cancelAccount',
    }
  },
  importantData: {
    id: 'preferences.editProfile.importantData',
  },
  deleteAccount: {
    title: {
      id: 'preferences.editProfile.deleteAccount',
    },
    warning: {
      id: 'preferences.editProfile.deleteAccountWarning',
    }
  },
  modal: {
    successfulUpdate: {
      id: 'preferences.modal.successfulUpdate.title',
    },
    ok: {
      id: 'preferences.modal.ok',
    }
  }
});

const validFormats = ['image/jpeg', 'image/jpg', 'image/png'];

export default class EditProfile extends Component<Props, State> {
  formValidator: FormValidator;

  constructor(props: Props) {
    super();
    this.formValidator = new FormValidator({
      firstName: {
        required: 'required',
      },
      lastName: {
        required: 'required',
      },
      image: (d) => d,
    });
    this.state = {
      image: { value: props.user.image || '', pristine: true },
      firstName: { value: props.user.firstName, pristine: true },
      lastName: { value: props.user.lastName, pristine: true },
      modalVisible: false,
    }
  }

  componentDidUpdate(prevProps: Props) {
    const { dataEditSuccessful, user } = this.props;
    if (prevProps.user !== user && user && dataEditSuccessful &&
      prevProps.user.languageCode === user.languageCode) {
      this.setState((state: State) => ({
        firstName: update(state.firstName, { pristine: { $set: true } }),
        lastName: update(state.lastName, { pristine: { $set: true } }),
        image: update(state.image, {
          pristine: { $set: true },
          value: { $set: user.image },
          $unset: ['previewImage']
        }),
        modalVisible: true,
      }));
    }
  }

  componentWillUnmount() {
    const { resetAlertState, resetEditProfileState } = this.props;
    resetAlertState();
    resetEditProfileState();
  }

  handleRejectedImage(rejectedFiles: File[]) {
    if (rejectedFiles.length) {
      if (rejectedFiles[0].type !== '' && validFormats.includes(rejectedFiles[0].type)) {
        this.setState((state: State) => update(state, {
          image: {
            errors: { $set: ['size'] },
            $unset: ['previewImage']
          }
        }));
      } else this.setState((state: State) => update(state, {
        image: {
          errors: { $set: ['format'] },
          $unset: ['previewImage']
        }
      }));
    }
  }

  handleChangeImage(acceptedFiles: File[]) {
    if (acceptedFiles.length) {
      const reader = new FileReader();
      reader.onload = () => {
        const fileAsDataURL = reader.result;
        this.setState((state: State) => (update(state, {
          image: {
            $unset: ['errors'],
            previewImage: { $set: fileAsDataURL.toString() },
            pristine: { $set: false }
          },
        })));
      };
      reader.onabort = () => console.log('file reading was aborted');
      reader.onerror = () => console.log('file reading has failed');
      reader.readAsDataURL(acceptedFiles[0]);
    }
  }

  isDataPristine(): boolean {
    return this.state.image.pristine && this.state.firstName.pristine && this.state.lastName.pristine;
  }

  handleChange(e: Event) {
    if (e.target instanceof HTMLInputElement) {
      const { name, value } = e.target;
      const pristine = this.props.user[name] === value;
      this.setState({ [name]: { value, pristine } });
    }
  }

  handleFocus(e: Event) {
    if (e.target instanceof HTMLInputElement) {
      const { name, value } = e.target;
      this.setState((state: State) => ({ [name]: update(state[name], { value: { $set: value } }) }));
    }
  }

  handleBlur(e: Event) {
    if (e.target instanceof HTMLInputElement) {
      const { name, value } = e.target;
      const errors = this.formValidator.validateField(name, value);
      if (errors.length) {
        this.setState({ [name]: { value, errors } });
      } else {
        this.setState((state: State) => ({ [name]: update(state[name], { value: { $set: value } }) }));
      }
    }
  }

  handleDataSubmit(e: Event) {
    e.preventDefault();
    const firstNameValue = this.state.firstName.value;
    const lastNameValue = this.state.lastName.value;
    const imagePreviewImage = this.state.image.previewImage;
    const errors = this.formValidator.validateForm({
      firstName: firstNameValue,
      lastName: lastNameValue,
    });
    if (errors.length) {
      errors.forEach((error: FormValidationResult) => {
        this.setState({
          [error.field]: {
            ...this.state[error.field],
            errors: error.errors,
          },
        });
      });
    } else {
      this.sendProfileData(imagePreviewImage);
    }
  }

  sendProfileData = (imagePreviewImage?: string) => {
    const { editProfile, editProfileImage } = this.props;
    const profileData: ProfileData = Object.keys(update(this.state, { $unset: ['image', 'isUploadingImage', 'modalVisible'] }))
      .filter((field: string) => !this.state[field].pristine)
      .reduce((acum: any, field: string) =>
        update(acum, { [field]: { $set: this.state[field].value } }), {});
    if (imagePreviewImage) {
      profileData.imageURI = this.state.image.previewImage;
      editProfileImage(profileData);
    } else {
      editProfile(profileData);
    }
  };

  handleCloseModal() {
    this.setState({ modalVisible: false });
  }

  render() {
    const { isPutting, isUploadingImage } = this.props;
    const { image, lastName, firstName, modalVisible } = this.state;
    const imageWarning = image.errors &&
      <label className={inputStyle.inputErrorLabel}>
        {this.context.intl.formatMessage(messages[image.errors[0]])}
      </label>;

    /*const nonUsedDeleteBlock = () => (
      <section key="delete" className={commonStyle.contentWrap}>
        <FormattedMessage id={messages.deleteAccount.title.id}>{
          txt => <h3>{txt}</h3>
        }</FormattedMessage>
        <form className={classNames(commonStyle.inputs)}
              onSubmit={(e) => e.preventDefault()}>
          <FormattedMessage id={messages.deleteAccount.warning.id}>{
            txt => <label className={commonStyle.warn}>{txt}</label>
          }</FormattedMessage>
          <FormattedMessage id={messages.button.cancelAccount.id}>{
            txt => <button
              type="button"
              className={classNames(buttonStyle.btn, buttonStyle.red)}
              onClick={/!*deleteProfile*!/() => console.log('deleting')}>{txt}</button>
          }</FormattedMessage>
        </form>
      </section>
    );*/

    return (
      <Fragment>
        <Helmet>
          <title>{this.context.intl.formatMessage(messages.title)}</title>
          <meta name="description" content={this.context.intl.formatMessage(messages.description)}/>
        </Helmet>
        <section className={classNames(boxStyle.box, commonStyle.contentWrap,
          { [style.loading]: isPutting || isUploadingImage })}>
          <FormattedMessage id={messages.profilePicture.id}>{
            txt => <h2 className={classNames(boxStyle.boxTitle, boxStyle.smallSpace)}>{txt}</h2>
          }</FormattedMessage>
          <FormattedMessage id={messages.profilePictureSubtitle.id}>{
            txt => <p className={boxStyle.subtitle}>{txt}</p>
          }</FormattedMessage>
          <Dropzone accept="image/jpeg,image/jpg,image/png"
                    multiple={false}
                    maxSize={10485760}
                    className={classNames(style.dropzone, { [style.error]: image && image.errors })}
                    activeClassName={style.active}
                    disabledClassName={style.disabled}
                    disabled={isUploadingImage}
                    onDrop={this.handleChangeImage.bind(this)}
                    onDropRejected={this.handleRejectedImage.bind(this)}>
            <i className={classNames(style.edit, icons.iconEditMedium)}/>
            <div className={style.veil}/>
            {image.previewImage &&
            <img src={image.previewImage}
                 alt="previewImage"
                 className={classNames(avatarStyle.avatar, avatarStyle.huge,
                   { [style.uploading]: isUploadingImage })}/>}
            {!isUploadingImage && !image.previewImage &&
            <img src={image.value || defaultPic}
                 alt="image"
                 className={classNames(avatarStyle.avatar, avatarStyle.huge)}/>}
            {isUploadingImage && <Loading className={style.loading}/>}
          </Dropzone>
          {imageWarning}
          <ImportantDataForm firstName={firstName}
                             lastName={lastName}
                             handleChange={this.handleChange.bind(this)}
                             handleFocus={this.handleFocus.bind(this)}
                             handleBlur={this.handleBlur.bind(this)}
                             isDataPristine={this.isDataPristine.bind(this)}
                             handleDataSubmit={this.handleDataSubmit.bind(this)}
                             isPutting={isPutting || isUploadingImage}/>
          {/*<article className={style.punchCardWrap}>
            <PunchCardContainer/>
          </article>*/}
        </section>
        {(isPutting || isUploadingImage) &&
        <Loading type="circle"
                 className={style.loading}/>}
        <SuccessErrorModal isOpen={modalVisible}
                           handleCloseModal={this.handleCloseModal.bind(this)}
                           titleInt={messages.modal.successfulUpdate.id}
                           color="lightBlue"
                           success={true}/>
      </Fragment>);
  }
}

EditProfile.propTypes = {
  editProfile: PropTypes.func.isRequired,
  editProfileImage: PropTypes.func.isRequired,
  resetEditProfileState: PropTypes.func.isRequired,
  user: PropTypes.object.isRequired,
  isPutting: PropTypes.bool,
  isUploadingImage: PropTypes.bool,
  putError: PropTypes.object,
  resetAlertState: PropTypes.func.isRequired,
  dataEditSuccessful: PropTypes.bool,
};

EditProfile.contextTypes = {
  intl: intlShape,
};



// WEBPACK FOOTER //
// ./src/modules/preferences/edit-profile/components/EditProfile.jsx